import { Icon } from '@iconify/react';
import { IconButton, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { FieldInputProps } from 'formik';
import * as React from 'react';
import { useState } from 'react';
import PasswordStrengthBar from 'react-password-strength-bar';
import { ERROR, PRIMARY } from 'theme/palette';
import {
  containsLowerCaseLetter,
  containsNumber,
  containsSpecialChars,
  containsUpperCaseLetter
} from 'utils/common';
import { IFormField } from '.';

export interface TextProps {
  item: IFormField;
  t: any;
  e: any;
  fieldProps: FieldInputProps<any>;
  fieldCallback: any;
}

export default function MFormText({ item, t, e, fieldProps, fieldCallback }: TextProps) {
  //past the rest of parameters in "item" to the TextField to avoild javscript errors
  const { name, handleChange, onBlur, type, dependentFields, dependantFieldValidation, ...rest } =
    item;

  const [showPassword, setShowPassword] = React.useState<boolean>(false);

  const handleShowPassword = (show: boolean) => {
    setShowPassword(!show);
  };

  const { value } = fieldProps;

  const IconStyle = styled(Icon)(({ theme }) => ({
    width: 20,
    height: 20,
    marginTop: 1,
    flexShrink: 0,
    marginRight: theme.spacing(2)
  }));

  const [minChar, setMinChar] = useState<boolean>(false);

  const [atleastOneLowerLetter, setAtleastOneLowerLetter] = useState<boolean>(false);

  const [atleastOneUpperLetter, setAtleastOneUpperLetter] = useState<boolean>(false);

  const [atleastOneNumber, setAtleastOneNumber] = useState<boolean>(false);

  const [atleastOneSpecialChar, setAtleastOneSpecialChar] = useState<boolean>(false);

  const getError = () => {
    return dependantFieldValidation ? Boolean(e) : Boolean(t && e);
  };
  const getHelpText = () => {
    return dependantFieldValidation ? e : t && e;
  };

  return (
    <>
      <TextField
        {...rest}
        key={name}
        fullWidth
        type={showPassword ? 'text' : type == 'percentage' ? 'number' : type}
        {...fieldProps}
        inputProps={{
          'data-testid': name
        }}
        InputProps={{
          ...(item.type === 'password'
            ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => handleShowPassword(showPassword)} edge="end">
                      <Icon icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                    </IconButton>
                  </InputAdornment>
                )
              }
            : item.type === 'percentage'
            ? {
                endAdornment: <InputAdornment position="end">%</InputAdornment>
              }
            : '')
        }}
        error={getError()}
        helperText={getHelpText()}
        onBlur={(event) => {
          const { value } = event.target;
          if (onBlur) onBlur(value);
        }}
        onChange={(event) => {
          const { value } = event.target;
          if (name === 'password') {
            setMinChar(value.length >= 8);
            setAtleastOneLowerLetter(containsLowerCaseLetter(value));
            setAtleastOneUpperLetter(containsUpperCaseLetter(value));
            setAtleastOneNumber(containsNumber(value));
            setAtleastOneSpecialChar(containsSpecialChars(value));
          }
          fieldCallback(item, value, dependentFields);
          if (handleChange) handleChange(event);
        }}
      />
      {name === 'password' && value && (
        <>
          <Stack spacing={2}>
            <PasswordStrengthBar password={value} minLength={8} />
            <Stack key={'password-minchar-check'} direction="row" alignItems="center">
              <IconStyle
                {...(minChar
                  ? { icon: 'eva:checkmark-fill', color: PRIMARY.main }
                  : { icon: 'eva:close-fill', color: ERROR.main })}
              />
              <Typography variant="body2">A minimum of 8 characters</Typography>
            </Stack>
            <Stack key={'password-atleastOneLowerLetter-check'} direction="row" alignItems="center">
              <IconStyle
                {...(atleastOneLowerLetter && atleastOneUpperLetter
                  ? { icon: 'eva:checkmark-fill', color: PRIMARY.main }
                  : { icon: 'eva:close-fill', color: ERROR.main })}
              />
              <Typography variant="body2">
                At least 1 upper case and 1 lower case letters
              </Typography>
            </Stack>
            <Stack key={'password-atleastOneNumber-check'} direction="row" alignItems="center">
              <IconStyle
                {...(atleastOneNumber
                  ? { icon: 'eva:checkmark-fill', color: PRIMARY.main }
                  : { icon: 'eva:close-fill', color: ERROR.main })}
              />
              <Typography variant="body2">At least 1 number</Typography>
            </Stack>
            <Stack key={'password-alphanumeric-check'} direction="row" alignItems="center">
              <IconStyle
                {...(atleastOneSpecialChar
                  ? { icon: 'eva:checkmark-fill', color: PRIMARY.main }
                  : { icon: 'eva:close-fill', color: ERROR.main })}
              />
              <Typography variant="body2">At least 1 special character</Typography>
            </Stack>
          </Stack>
        </>
      )}
    </>
  );
}
